import {
  FONTES_DE_TEXTO,
  estiloCssDaFonte,
  familiaCssDaFonte,
  fonteDeTexto,
  regrasFontFace,
  type CamadaDeTexto,
} from "@foto/shared";

/**
 * As fontes dos textos sobre a foto no navegador.
 *
 * A tela e o servidor usam **o mesmo arquivo de fonte**: o servidor o lê do disco para desenhar o JPEG, e a
 * tela o baixa de `/api/fontes/<arquivo>` por `@font-face`. As regras (`regrasFontFace`) saem do registro
 * em `@foto/shared`, e entram na página **uma vez** numa `<style>`.
 *
 * Declarar a regra não baixa nada: o navegador só busca o arquivo quando algum texto da página usa aquela
 * família, peso e estilo. Por isso as 13 famílias podem ficar declaradas desde o início sem custo.
 */

export const ID_DO_ESTILO_DAS_FONTES = "fontes-das-camadas";

/** As regras `@font-face` de todas as famílias do pacote. */
export const cssDasFontes = (): string => FONTES_DE_TEXTO.map((f) => regrasFontFace(f)).join("\n");

/** Põe as regras na página, uma vez. Chamar de novo não faz nada. */
export function injetarFontes(doc: Document = document): void {
  if (doc.getElementById(ID_DO_ESTILO_DAS_FONTES)) return;
  const estilo = doc.createElement("style");
  estilo.id = ID_DO_ESTILO_DAS_FONTES;
  estilo.textContent = cssDasFontes();
  doc.head.appendChild(estilo);
}

/**
 * O descritor que `document.fonts.load()` entende para uma camada de texto (`italic 700 16px "Texto Archivo"`):
 * o mesmo peso e o mesmo itálico efetivos que o CSS da camada usa, para carregar exatamente o arquivo que o texto
 * vai pedir.
 */
export function descritorDaFonte(
  camada: Pick<CamadaDeTexto, "fonteId" | "peso" | "italico">,
): string | null {
  const fonte = fonteDeTexto(camada.fonteId);
  if (!fonte) return null;
  const css = estiloCssDaFonte(fonte, camada.peso, camada.italico);
  return `${css.fontStyle} ${css.fontWeight} 16px "${familiaCssDaFonte(fonte)}"`;
}

/** O descritor de cada combinação de fonte, peso e estilo usada pelas camadas de texto, sem repetir. */
export function descritoresDasCamadas(
  camadas: readonly Pick<CamadaDeTexto, "fonteId" | "peso" | "italico">[],
): string[] {
  const vistos = new Set<string>();
  for (const c of camadas) {
    const d = descritorDaFonte(c);
    if (d) vistos.add(d);
  }
  return [...vistos];
}

/**
 * Pede ao navegador o arquivo da fonte e espera. Nunca rejeita: sem `document.fonts` (ou com o arquivo fora
 * do ar) a tela segue na fonte de reserva, e o texto na folha impressa sai na certa de qualquer jeito.
 */
export async function carregarFonte(descritor: string, texto: string): Promise<void> {
  try {
    await document.fonts?.load(descritor, texto);
  } catch {
    // Fonte de reserva: o texto aparece mesmo assim.
  }
}
